import { Box, Typography, Autocomplete, TextField } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { Edit as EditIcon, NoteAddOutlined as NoteIcon } from '@mui/icons-material';
import ActionButton, { MenuOption } from '../../../../../components/ActionButton';
import {
  GoalGridRowData,
  SeverityStatusDataWithDate,
} from '../../../../../@types/crs/case';
import SeverityStatus from 'src/components/SeverityStatus';
import moment from 'moment';
import { ValueSetComposeIncludeConcept } from 'src/careflow/ph-core/fhir-client/resources';
import PeriodFilter, { onDateChange } from 'src/components/PeriodFilter';
import CheckboxList from 'src/components/CheckboxList';
import CaseTaskGrid from 'src/careflow/care-coordination/case/detail/components/CaseTaskGrid';
import CollapsibleDataGrid from 'src/careflow/ph-core/grid/react/CollapsibleDataGrid';
import GoalModal, { GoalLifeCycleStatusOption } from './GoalModal';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { WrappedCarePlan } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/CarePlan';
import useObjectState from 'src/hooks/useObjectState';
import { WrappedGoal } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Goal';
import { WrappedPlanDefinition } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/PlanDefinition';
import React, { useEffect, useMemo } from 'react';
import { momentDateComparator } from 'src/utils/dates';
import useValueSetsByIdentifiers from 'src/hooks/useValueSetsByIdentifier';
import ModalCommunication from 'src/careflow/common/ModalCommunication';
import { WrappedCondition } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Condition';
import { useTheme } from '@mui/material/styles';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import GoalConditionList from './GoalConditionList';
import { OnConditionUnlink } from './GoalConditionDrawer';
import useClientGrid from 'src/careflow/ph-core/grid/hook/useClientGrid';
import usePermissionsContext from 'src/careflow/ph-core/admin/permissions/hook/usePermissionsContext';
import { GoalsPermissions } from 'src/careflow/helpers/types';
import { PermissionsProvider } from 'src/careflow/ph-core/admin/permissions/context/PermissionsContext';
import useCollapsibleContext from 'src/hooks/useCollapsibleContext';
import GoalsGridWithFilters from './GoalsGridWithFilters';
import GoalContextProvider from '../provider';
import { PARENT_GOAL_SUCCESSOR_RELATIONSHIP_CODING } from 'src/careflow/helpers/constants';
import { getGoalRelationExtensionPayload } from 'src/utils/fhir';
import CellRow from 'src/careflow/common/CellRow';
import { WrappedObservation } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Observation';
import { getRegistries } from 'src/careflow/ph-core/multi-tenant/registry/registry';
import { useQuery } from '@tanstack/react-query';
import TaskContextProvider from 'src/careflow/care-coordination/task/common/provider';
import { filterTasksByDefinition } from 'src/careflow/helpers/common';
import { commonMenuTaskHandlers } from 'src/careflow/care-coordination/task/common/handlers';
import ServiceRequestContextProvider from 'src/careflow/care-coordination/referral/components/ServiceRequestProvider';
import GoalDetailDrawer from './GoalDetailDrawer';
import { CREATE_GOAL, UPDATE_GOAL, useEventBus } from 'src/event-bus';
import useGoalContext from 'src/careflow/care-coordination/case/goal/hook/useGoalContext';
import { GridFilterDrawerProps } from 'src/careflow/ph-core/grid/react/DataGridWithFilters/DataGridWithFilters';
import useGridFilterConsolidation from 'src/careflow/ph-core/grid/hook/useGridFilterConsolidation';

export interface GoalFilters {
  selectedPathways: ValueSetComposeIncludeConcept[];
  startDate: moment.Moment | null;
  endDate: moment.Moment | null;
  status: GoalLifeCycleStatusOption[];
}

export interface GoalsGridBaseProps {
  pathways: ValueSetComposeIncludeConcept[];
  defaultPathway?: ValueSetComposeIncludeConcept;
  goals: GoalGridRowData[];
  showPathwayFilter?: boolean;
  patient: WrappedPatient | null;
  carePlan?: WrappedCarePlan | null;
  planDefinitions: WrappedPlanDefinition[];
  conditions: WrappedCondition[];
  observations: WrappedObservation[];
  showCategory?: boolean;
  title?: string;
  onConditionUnlink: OnConditionUnlink;
  statusesList: any;
  includeSubGoals?: boolean;
  isNestedGrid?: boolean;
  showPagination?: boolean;
  noOfSubGrids?: number;
  addButtonTitle?: string;
  includeSubTasks?: boolean;
}

export interface GoalsGridProps extends GoalsGridBaseProps, GridFilterDrawerProps<GoalFilters> {}

export const initialGoalFilters: GoalFilters = {
  selectedPathways: [],
  startDate: null,
  endDate: null,
  status: [],
};

const GoalsGrid = ({
  goals,
  pathways,
  filterValues,
  showPathwayFilter = true,
  onSearchTextFieldChange,
  searchTextFieldValue,
  onFilterDrawerOpen,
  onFilterDrawerClose,
  isFilterDrawerOpen,
  onApplyFilters,
  patient,
  defaultPathway,
  carePlan,
  planDefinitions,
  conditions,
  observations,
  showCategory = false,
  title,
  onConditionUnlink,
  statusesList,
  includeSubGoals = false,
  isNestedGrid,
  showPagination,
  noOfSubGrids,
  addButtonTitle,
  includeSubTasks = true,
}: GoalsGridProps) => {
  const { filters, updateFilters, onClearAllFilters } = useGridFilterConsolidation<GoalFilters>(
    filterValues,
    { ...initialGoalFilters }
  );
  const { i18n } = useLocales();
  const { isWorkflowContext } = useGoalContext();
  const { dispatchEvent } = useEventBus();

  const theme = useTheme();

  const { handleItem } = useCollapsibleContext();

  useEffect(() => handleItem?.(statusesList), [statusesList]);

  const {
    tasks: tasksPermissions,
    notes: notesPermissions,
    isAllowedToEdit,
  } = usePermissionsContext<GoalsPermissions>() ?? {};

  const {
    valueSets: [goalStatuses],
  } = useValueSetsByIdentifiers(['ph-goal-lifecycle-status']);

  const { sortModel, handleSortModelChange } = useClientGrid({
    defaultSort: [{ field: 'endDate', sort: 'asc' }],
  });

  const [{ 
      isGoalModalOpen, 
      goalToEdit, 
      addNoteToTask, 
      isGoalDetailDrawerOpen 
    }, 
    updateState
  ] = useObjectState<{
    isGoalModalOpen: boolean;
    goalToEdit: WrappedGoal | null;
    addNoteToTask: boolean;
    isGoalDetailDrawerOpen: boolean;
  }>({
    goalToEdit: null,
    isGoalModalOpen: false,
    addNoteToTask: false,
    isGoalDetailDrawerOpen: false
  });

  const getMenuOptions = (goal: WrappedGoal): MenuOption[] => [
    ...(isAllowedToEdit
      ? [
          {
            IconComponent: EditIcon,
            title: 'Edit',
            callback: () => {
              updateState({
                isGoalModalOpen: true,
                goalToEdit: goal,
              });
            },
            iconColor: theme.palette.primary.main,
          },
        ]
      : []),
    ...(notesPermissions?.isAllowedToView
      ? [
          {
            IconComponent: NoteIcon,
            title: 'Notes',
            callback: () => {
              updateState({
                addNoteToTask: true,
                goalToEdit: goal,
              });
            },
            iconColor: theme.palette.primary.main,
          },
        ]
      : []),
  ];

  const columns: GridColDef[] = [
    {
      field: 'goal',
      headerName: 'Goal',
      flex: 2,
      renderCell: (params) => {
        const { goal } = params.row as GoalGridRowData;
        return <CellRow title={goal} />;
      },
    },
    {
      field: 'status',
      headerName: `${i18n('case.details.listofGoals.status', 'crs')}`,
      flex: 1,
      renderCell: (params) => {
        const { status } = params.row as GoalGridRowData;
        return <CellRow title={status} />;
      },
    },
    {
      field: 'conditions',
      headerName: `${i18n('case.details.listofGoals.condition', 'crs')}`,
      flex: 1,
      renderCell: (params) => (
        <GoalConditionList
          observations={params.row.observations}
          conditions={params.row.conditions}
          goal={params.row.wrappedGoal}
          onConditionUnlink={onConditionUnlink}
        />
      ),
    },
    ...(showCategory
      ? [
          {
            field: 'pathwayDisplay',
            headerName: `${i18n('case.details.listofGoals.category', 'crs')}`,
            flex: 1,
          },
        ]
      : []),
    {
      field: 'endDate',
      headerName: `${i18n('case.details.listofGoals.endDate', 'crs')}`,
      flex: 1,
      sortComparator: (_, __, cellParams1, cellParams2) => {
        const { date: date1 } = (cellParams1?.value as SeverityStatusDataWithDate) ?? {};
        const { date: date2 } = (cellParams2?.value as SeverityStatusDataWithDate) ?? {};
        return momentDateComparator(date1, date2);
      },
      renderCell: (params) => {
        const { endDate } = params.row as GoalGridRowData;
        return <SeverityStatus status={endDate} />;
      },
    },
    {
      field: 'edit',
      headerName: '',
      flex: 0.5,
      sortable: false,
      renderCell: (params) => {
        const { wrappedGoal } = params.row as GoalGridRowData;
        return <ActionButton menuOptions={getMenuOptions(wrappedGoal)} />; //TODO need to add functionality to open edit modal
      },
    }
  ];

  const handleDateFilterChange =
    (dateProp: 'endDate' | 'startDate'): onDateChange =>
    (newValue) => {
      updateFilters({ [dateProp]: newValue });
    };
  const handlePathwaySelection: OnPathwaySelectionChange = (_, newValue) => {
    updateFilters({ selectedPathways: newValue });
  };

  const { startDate, endDate, selectedPathways, status } = filters;

  const { data: taskADRegistry } = useQuery({
    queryKey: ['taskActivityDefinition'],
    queryFn: async () => {
      const taskActivityDefinition = await getRegistries(undefined, 'task-activity-definition');
      return taskActivityDefinition;
    },
  });

  const taskActivityDefinition = useMemo(
    () => (taskADRegistry?.[0]?.keyValue ? JSON.parse(taskADRegistry?.[0]?.keyValue) : null),
    [taskADRegistry]
  );

  const handleOpenDrawerGoal = (goal: WrappedGoal | null) => {
    updateState({ isGoalDetailDrawerOpen: true, goalToEdit: goal });
  };

  return (
    <>
      <CollapsibleDataGrid
        noOfSubGrids={noOfSubGrids}
        isNestedGrid={isNestedGrid}
        showPagination={showPagination}
        getRowHeight={() => 'auto'}
        addButtonTitle={addButtonTitle ?? i18n('case.details.listofGoals.button', 'crs')}
        title={title ?? i18n('case.details.listofGoals.title', 'crs')}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        onAddButtonClick={() => {
          updateState({
            isGoalModalOpen: true,
            goalToEdit: null,
          });
        }}
        onFilterDrawerOpen={onFilterDrawerOpen}
        rows={goals}
        columns={columns}
        getRowId={(row) => row.id}
        onSearchTextFieldChange={onSearchTextFieldChange}
        searchTextFieldValue={searchTextFieldValue}
        onRowDoubleClick={(params) => {
          const { wrappedGoal } = params.row as GoalGridRowData;
          handleOpenDrawerGoal(wrappedGoal);
        }}
        renderCollapsibleContent={({ 
          tasks, 
          id, 
          wrappedGoal, 
          subGoals, 
          observations: observationsSuggestions, 
          conditions: conditionSuggestions
        }: GoalGridRowData) => {
          const taskFilterByActivityDefinition = filterTasksByDefinition(
            tasks,
            taskActivityDefinition?.caseGoal
          );
          return (
            <>
              <TaskContextProvider
                menuTaskHandlers={commonMenuTaskHandlers}
                taskActivityDefinitionContext={taskActivityDefinition?.caseGoal}
              >
                <ServiceRequestContextProvider
                  observations={observationsSuggestions}
                  conditions={conditionSuggestions}
                >
                  <PermissionsProvider permissions={tasksPermissions ?? null}>
                    <CaseTaskGrid
                      patient={patient}
                      carePlan={carePlan}
                      includeSubTasks={includeSubTasks}
                      tasks={taskFilterByActivityDefinition}
                      isNestedGrid={true}
                      showPagination={false}
                      goal={wrappedGoal}
                      conditions={conditions}
                      observations={observations}
                      goalTaskSubTask={false}
                      title={i18n('patients.details.tasks.title', 'crs')}
                      buttonTitle={i18n('patients.details.tasks.button', 'crs')}
                    />
                  </PermissionsProvider>
                </ServiceRequestContextProvider>
              </TaskContextProvider>
              {!!includeSubGoals && (
                <GoalContextProvider
                  relatedGoals={[
                    {
                      extension: [
                        getGoalRelationExtensionPayload(
                          PARENT_GOAL_SUCCESSOR_RELATIONSHIP_CODING,
                          id
                        ),
                      ],
                      wrappedGoal,
                    },
                  ]}
                >
                  <Box sx={{ marginTop: 3, width: '100%', padding: 0 }}>
                    <GoalsGridWithFilters
                      isNestedGrid={true}
                      showPagination={false}
                      includeSubGoals={false}
                      showCategory={true}
                      planDefinitions={planDefinitions}
                      conditions={conditions}
                      observations={observations ?? []}
                      patient={patient as WrappedPatient}
                      goals={subGoals ?? []}
                      defaultPathway={defaultPathway}
                      pathways={pathways}
                      showPathwayFilter={true}
                      carePlan={carePlan as WrappedCarePlan}
                      includeSubTasks={false}
                      statusesList={statusesList}
                      addButtonTitle={i18n('case.details.listofGoals.newSubGoal', 'crs')}
                      title={i18n('case.details.listofGoals.subGoals', 'crs')}
                    />
                  </Box>
                </GoalContextProvider>
              )}
            </>
          );
        }}
        Filters={
          <GoalFilter
            selectedStatuses={status}
            selectedPathways={selectedPathways}
            showPathwayFilter={showPathwayFilter}
            pathways={pathways}
            endDate={endDate}
            startDate={startDate}
            goalStatuses={goalStatuses?.asList?.() ?? []}
            onEndDateChange={handleDateFilterChange('endDate')}
            onStartDateChange={handleDateFilterChange('startDate')}
            onPathwaySelectionChange={handlePathwaySelection}
            onStatusSelectionChange={(checkedItems) => {
              updateFilters({
                status: checkedItems,
              });
            }}
          />
        }
        FilterDrawerProps={{
          title: 'Goals Filters',
          open: isFilterDrawerOpen,
          onApplyButtonClick: () => {
            onApplyFilters(filters);
          },
          onCloseIconButtonClick: onFilterDrawerClose,
          onClearAllButtonClick: onClearAllFilters,
        }}
      />
      <GoalModal
        planDefinitions={planDefinitions}
        conditions={conditions}
        observations={observations}
        goalToEdit={goalToEdit}
        onSuccessfulEdit={() => dispatchEvent(UPDATE_GOAL, {isWorkflowContext})}
        onSuccessfulCreation={() => dispatchEvent(CREATE_GOAL, {isWorkflowContext})}
        defaultPathway={defaultPathway}
        pathways={pathways}
        patient={patient}
        open={isGoalModalOpen}
        carePlan={carePlan}
        onClose={() => {
          updateState({
            isGoalModalOpen: false,
          });
        }}
      />
      <ModalCommunication
        isAllowedToAdd={!!notesPermissions?.isAllowedToAdd}
        open={addNoteToTask}
        patient={patient}
        onCancel={() => {
          updateState({ addNoteToTask: false });
        }}
        carePlan={carePlan}
        resource={goalToEdit}
        typeNote="notes_goal"
        i18n={i18n}
      />
      <GoalDetailDrawer 
        open={isGoalDetailDrawerOpen}
        goal={goalToEdit}
        onCloseIconButtonClick={() => {
          updateState({ isGoalDetailDrawerOpen: false, goalToEdit: null });
        }}
        title={i18n('case.details.listofGoals.titleDrawer', 'crs')}
      />
    </>
  );
};

export type OnPathwaySelectionChange = (
  event: React.SyntheticEvent,
  newValue: ValueSetComposeIncludeConcept[]
) => void;
interface GoalFilterProps {
  showPathwayFilter?: boolean;
  pathways: ValueSetComposeIncludeConcept[];
  startDate: moment.Moment | null;
  endDate: moment.Moment | null;
  selectedPathways: ValueSetComposeIncludeConcept[];
  onStartDateChange: onDateChange;
  onEndDateChange: onDateChange;
  onPathwaySelectionChange: OnPathwaySelectionChange;
  onStatusSelectionChange: (checkedStatus: GoalLifeCycleStatusOption[]) => void;
  selectedStatuses: GoalLifeCycleStatusOption[];
  goalStatuses: GoalLifeCycleStatusOption[];
}

const GoalFilter = ({
  showPathwayFilter = true,
  pathways,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onPathwaySelectionChange,
  selectedPathways,
  onStatusSelectionChange,
  selectedStatuses,
  goalStatuses,
}: GoalFilterProps) => (
  <>
    <Box py={3}>
      {showPathwayFilter && (
        <Box>
          <Typography fontSize={'1rem'} color={'#919eab'} mb={1}>
            Pathway
          </Typography>

          <Autocomplete
            value={selectedPathways}
            popupIcon={null}
            fullWidth
            multiple
            onChange={onPathwaySelectionChange}
            options={pathways}
            getOptionLabel={(option) => option?.display || ''}
            defaultValue={[]}
            renderInput={(params) => <TextField {...params} variant="outlined" />}
          />
        </Box>
      )}
      <Box marginTop={showPathwayFilter ? 3 : 0} marginBottom={1}>
        <PeriodFilter
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={onStartDateChange}
          onEndDateChange={onEndDateChange}
        />
      </Box>
    </Box>
    <CheckboxList
      containerSx={{ marginBottom: 4 }}
      title="Status"
      keyExtractor={(item) => item.code as string}
      labelExtractor={(item) => item.display}
      onChange={onStatusSelectionChange}
      options={goalStatuses}
      externalCheckedItems={selectedStatuses}
    />
  </>
);

export default GoalsGrid;
